import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Select, { Option } from 'components/Common/Select';
import { Box, Chip, Drawer, IconButton, Pagination, Stack, Typography, useTheme } from '@mui/material';
import { MdInfoOutline, MdClose } from 'react-icons/md';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from 'components/Common/DataTable';
import PaginationComponent, { usePagination } from 'components/Common/Pagination';
import DateRangePicker from 'components/Common/DateRangePicker';
import { useGetAgencySynchronizerLogs, useGetAgencySynchronizedItems } from '@/apis/agency/syncLogs/hook';
import { useGetBusinesses } from '@/apis/agency/hook';
import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';

interface FilterFormData {
  filter: {
    integration_business_id_eq: string;
    integration_id_eq: string;
    sync_type_eq: string;
    status_eq: string;
    created_at_in: string;
  };
}

const defaultFormValues: FilterFormData = {
  filter: {
    integration_business_id_eq: ' ',
    integration_id_eq: ' ',
    sync_type_eq: ' ',
    status_eq: ' ',
    created_at_in: ''
  }
};

const SYNC_TYPES = [
  { value: ' ', label: 'All' },
  { value: 'daily', label: 'Daily' },
  { value: 'initial', label: 'Initial' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'post_call', label: 'PostCall' },
  { value: 'manual', label: 'Manual' }
];

const STATUSES = [
  { value: ' ', label: 'All' },
  { value: 'started', label: 'Started' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'partial', label: 'Partial' }
];

function formatDateTime(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return iso ?? '—';
  }
}

export default function AgencySyncLogs() {
  const theme = useTheme();
  const { control, handleSubmit, watch } = useForm<FilterFormData>({
    defaultValues: defaultFormValues
  });

  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();
  const [itemsDrawerOpen, setItemsDrawerOpen] = useState(false);
  const [selectedSynchronizerLogId, setSelectedSynchronizerLogId] = useState<number | null>(null);
  const [itemsPage, setItemsPage] = useState(1);

  const [businessesOptions, setBusinessesOptions] = useState<Option[]>([]);
  const [businessListPage, setBusinessListPage] = useState(1);

  const { data: businesses } = useGetBusinesses({
    page: businessListPage,
    filter: { account_status_eq: 0 }
  });
  const { data: agencyOrganization } = useGetAgencyOrganizations();

  useEffect(() => {
    if (businesses?.data) {
      setBusinessesOptions((prev) => {
        const existingIds = new Set(prev.map((o) => o.value));
        const newOptions = businesses.data
          .filter((item: { id: number }) => !existingIds.has(item.id))
          .map((item: { id: number; name: string }) => ({ label: item.name, value: item.id }));
        return [...prev, ...newOptions];
      });
    }
  }, [businesses]);

  const [appliedFilters, setAppliedFilters] = useState<FilterFormData>(defaultFormValues);

  // Sync form selections to table filters when user changes dropdowns (stable deps to avoid infinite loop)
  const watchedValues = watch();
  const watchedFiltersStr = JSON.stringify(watchedValues?.filter ?? {});
  useEffect(() => {
    setAppliedFilters(watch());
    resetPage();
  }, [watchedFiltersStr, watch, resetPage]);

  const onSubmit = (data: FilterFormData) => {
    setAppliedFilters(data);
    resetPage();
  };

  const buildParams = () => {
    const f = appliedFilters.filter as Record<string, unknown>;
    const params: Record<string, unknown> = { page: currentPage, per_page: perPage };
    const str = (v: unknown) => (v != null ? String(v).trim() : '');
    if (str(f?.integration_business_id_eq))
      params['filter[integration_business_id_eq]'] = str(f.integration_business_id_eq);
    if (str(f?.integration_id_eq)) params['filter[integration_id_eq]'] = str(f.integration_id_eq);
    if (str(f?.sync_type_eq)) params['filter[sync_type_eq]'] = str(f.sync_type_eq);
    if (str(f?.status_eq)) params['filter[status_eq]'] = str(f.status_eq);
    const createdIn = str(f?.created_at_in);
    if (createdIn) {
      const [start, end] = createdIn.split(',');
      if (start) params['filter[created_at_gteq]'] = start.trim();
      if (end) params['filter[created_at_lteq]'] = end.trim();
    }
    return params;
  };

  const { data: logsResponse, isLoading: isLoadingLogs } = useGetAgencySynchronizerLogs(buildParams());
  const { data: itemsResponse, isLoading: isLoadingItems } = useGetAgencySynchronizedItems(selectedSynchronizerLogId, {
    page: itemsPage
  });

  const openItemsDrawer = (logId: number) => {
    setSelectedSynchronizerLogId(logId);
    setItemsPage(1);
    setItemsDrawerOpen(true);
  };

  const closeItemsDrawer = () => {
    setItemsDrawerOpen(false);
    setSelectedSynchronizerLogId(null);
    setItemsPage(1);
  };

  const tableData = (logsResponse?.data ?? []).map((row) => ({
    id: row.id,
    date_time: formatDateTime(row.started_at),
    status: row.status,
    duration: row.total_duration,
    api_hits: row.api_hits,
    total: row.total_synced_count,
    new: row.metadata?.total_created_count,
    updated: row.metadata?.total_updated_count,
    failed: row.total_failed_count,
    business_name: row.business_name,
    integration_name: row.integration_name,
    sync_type: row.sync_type,
    entity_type: row.entity_type
  }));

  const columns: ColumnDef<(typeof tableData)[0]>[] = [
    { header: 'DATE & TIME', accessorKey: 'date_time', size: 180 },
    {
      header: 'BUSINESS',
      accessorKey: 'business_name',
      size: 140,
      cell: ({ row }) => <Typography sx={{ fontSize: 14 }}>{row.original.business_name ?? '—'}</Typography>
    },
    {
      header: 'INTEGRATION',
      accessorKey: 'integration_name',
      size: 120,
      cell: ({ row }) => (
        <Typography sx={{ fontSize: 14, textTransform: 'capitalize' }}>
          {row.original.integration_name ?? '—'}
        </Typography>
      )
    },
    {
      header: 'SYNC TYPE',
      accessorKey: 'sync_type',
      size: 100,
      cell: ({ row }) => (
        <Typography sx={{ fontSize: 14, textTransform: 'capitalize' }}>
          {row.original.sync_type?.replace('_', ' ') ?? '—'}
        </Typography>
      )
    },
    {
      header: 'STATUS',
      accessorKey: 'status',
      size: 110,
      cell: ({ row }) => {
        const status = row.original.status;
        const colorMap: Record<string, { text: string; bg: string }> = {
          started: {
            text: theme.palette.info.dark ?? '#0d47a1',
            bg: theme.palette.info.light
          },
          running: { text: theme.palette.primary.main, bg: theme.palette.primary.light },
          completed: { text: theme.palette.success.main, bg: theme.palette.success.light },
          failed: { text: theme.palette.error.main, bg: theme.palette.error.light },
          partial: { text: theme.palette.warning.main, bg: theme.palette.warning.light }
        };
        const style = colorMap[status] ?? { text: '#374151', bg: '#F3F4F6' };
        const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : '—';
        return (
          <Chip
            label={label}
            size='small'
            sx={{ fontWeight: 500, borderRadius: '10px', color: style.text, bgcolor: style.bg }}
          />
        );
      }
    },
    { header: 'DURATION', accessorKey: 'duration', size: 90 },
    {
      header: 'API HITS',
      accessorKey: 'api_hits',
      size: 100,
      cell: ({ row }) => (
        <Box
          display='flex'
          alignItems='center'
          gap={0.5}
          sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
          onClick={(e) => {
            e.stopPropagation();
            openItemsDrawer(row.original.id);
          }}
        >
          {row.original.api_hits}
          <MdInfoOutline size={16} />
        </Box>
      )
    },
    { header: 'TOTAL', accessorKey: 'total', size: 70 },
    {
      header: 'NEW',
      accessorKey: 'new',
      size: 70,
      cell: ({ row }) => <Typography color='success.main'>{row.original.new ?? '—'}</Typography>
    },
    {
      header: 'UPDATED',
      accessorKey: 'updated',
      size: 90,
      cell: ({ row }) => <Typography color='primary.main'>{row.original.updated ?? '—'}</Typography>
    },
    {
      header: 'FAILED',
      accessorKey: 'failed',
      size: 80,
      cell: ({ row }) => (
        <Typography color='error.main'>{row.original.failed === 0 ? '—' : row.original.failed}</Typography>
      )
    }
  ];

  return (
    <>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        marginBottom={3}
        flexDirection={{ xs: 'column', lg: 'row' }}
        gap={{ xs: 2, lg: 0 }}
        sx={{ width: '100%' }}
      >
        <Typography variant='h6' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
          Sync Logs
        </Typography>
        <Box
          component='form'
          onSubmit={handleSubmit(onSubmit)}
          display='flex'
          gap={1}
          flexDirection={{ xs: 'column', lg: 'row' }}
          flexWrap='wrap'
          sx={{ width: { xs: '100%', lg: 'auto' } }}
        >
          <Box sx={{ width: { xs: '100%', lg: '180px' } }}>
            <Select
              name='filter.integration_business_id_eq'
              label='Business'
              control={control}
              options={[{ value: ' ', label: 'All' }, ...businessesOptions]}
              onScrollEnd={() => {
                if (businessListPage < (businesses?.meta?.total_pages ?? 1)) {
                  setBusinessListPage((p) => p + 1);
                }
              }}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '180px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '160px' } }}>
            <Select
              name='filter.sync_type_eq'
              label='Sync Type'
              control={control}
              options={SYNC_TYPES}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '160px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '140px' } }}>
            <Select
              name='filter.status_eq'
              label='Status'
              control={control}
              options={STATUSES}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '140px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '270px' }, minWidth: { xs: '100%', lg: '270px' } }}>
            <DateRangePicker name='filter.created_at_in' control={control} createdAt={agencyOrganization?.created_at} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ width: '100%', overflowX: 'auto', mb: { xs: 2, lg: 0 } }}>
        <DataTable data={tableData} columns={columns} isHeaderSticky isLoading={isLoadingLogs} />
      </Box>
      <Box sx={{ pb: { xs: 2, lg: 0 } }}>
        <PaginationComponent
          totalPages={logsResponse?.meta?.total_pages ?? 1}
          totalCount={logsResponse?.meta?.total_count ?? 0}
          currentPage={currentPage}
          perPage={perPage}
          onChange={setPage}
          onPerPageChange={setPerPage}
        />
      </Box>

      <Drawer
        anchor='right'
        open={itemsDrawerOpen}
        onClose={closeItemsDrawer}
        slotProps={{
          backdrop: { invisible: false },
          paper: { sx: { width: { xs: '100%', sm: 480 }, maxWidth: '100%' } }
        }}
      >
        <Box height='100%' display='flex' flexDirection='column' bgcolor='background.paper'>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            px={2}
            py={2}
            borderBottom='1px solid'
            borderColor='divider'
          >
            <Box>
              <Typography fontSize={18} fontWeight={600}>
                Synchronized Items
              </Typography>
              <Typography fontSize={13} color='text.secondary'>
                {isLoadingItems
                  ? 'Loading…'
                  : `${itemsResponse?.meta?.total_count ?? 0} requests made during this sync`}
              </Typography>
            </Box>
            <IconButton size='small' onClick={closeItemsDrawer} aria-label='Close'>
              <MdClose size={22} />
            </IconButton>
          </Box>
          <Box flex={1} overflow='auto' px={2} py={2}>
            {isLoadingItems ? (
              <Typography color='text.secondary' fontSize={14}>
                Loading…
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {(itemsResponse?.data ?? []).map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'grey.50'
                    }}
                  >
                    <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap' mb={0.5}>
                      <Chip
                        label={item.status ?? '—'}
                        size='small'
                        sx={{
                          fontWeight: 600,
                          bgcolor: item.status?.startsWith?.('2')
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          color: theme.palette.common.white,
                          height: 22
                        }}
                      />
                      <Typography component='span' fontSize={13} fontWeight={500}>
                        GET
                      </Typography>
                      <Typography fontSize={12} color='text.secondary' sx={{ wordBreak: 'break-all' }}>
                        {item.request_endpoint}
                      </Typography>
                    </Stack>
                    <Typography fontSize={13} color='text.secondary'>
                      Fetched {item.fetched_items_count ?? item.payload_snapshot?.synced_count ?? 0} contact records
                      successfully.
                    </Typography>
                    <Typography fontSize={12} color='text.secondary' mt={0.5}>
                      {item.api_duration_in_ms}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
          {itemsResponse?.meta && itemsResponse.meta.total_pages > 1 && (
            <Box px={2} py={2} borderTop='1px solid' borderColor='divider' display='flex' justifyContent='center'>
              <Pagination
                count={itemsResponse.meta.total_pages}
                page={itemsPage}
                onChange={(_, newPage) => setItemsPage(newPage)}
                color='primary'
                size='medium'
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
}
